<template>
  <span class="flex justify-center">
    <span
      class="flex items-center justify-center w-6 h-6 rounded-full"
      :style="{ backgroundColor: config.bg }"
    >
      <svg :class="['w-4 h-4', config.colorClass]" viewBox="0 0 24 24" aria-hidden="true">
        <path :d="config.icon" fill="currentColor" />
      </svg>
    </span>

    <v-tooltip activator="parent" location="top" :text="tooltipText" />
  </span>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { mdiAlert, mdiCheck, mdiClose, mdiMinus, mdiTimerSand } from '@mdi/js'

import { EffectiveRegistrationStatus } from '@/constants/ds-attendance.constants'

import { COMPLIANCE_STATE_TITLES, REGISTRATION_STATUS_LABELS } from '../constants'
import type { ComplianceState, RegistrationStatusValue } from '../types'

const props = defineProps<{
  state: ComplianceState
  // Sem o rótulo do domínio o tooltip mostra só o estado — a célula da tabela
  // passa o rótulo para o texto ficar autoexplicativo ("Repasse — Conforme").
  domainLabel?: string
  /**
   * F4 (M5, balão ①) — só a coluna "Reg. Plantão" manda este valor, e só quando
   * o backend classificou o plantão. Presente ⇒ a bolinha usa os QUATRO estados
   * do registro; ausente ⇒ o comportamento de hoje, intocado. É o que mantém os
   * outros quatro domínios exatamente como estão.
   */
  registrationStatus?: string | null
}>()

interface StateConfig {
  icon: string
  colorClass: string
  bg: string
}

// O texto do tooltip vem de COMPLIANCE_STATE_TITLES — compartilhado com os
// aria-labels das células de domínio para o estado ser anunciado igual.
const CONFIG: Record<ComplianceState, StateConfig> = {
  ok: { icon: mdiCheck, colorClass: 'text-sucess', bg: '#EAFBEF' },
  fail: { icon: mdiClose, colorClass: 'text-negative', bg: '#FCE9EA' },
  pending: { icon: mdiMinus, colorClass: 'text-neutral50', bg: '#F0F0F4' },
  // Q1 — neutro: o traço cinza diz "não se aplica", nunca "falhou".
  not_required: { icon: mdiMinus, colorClass: 'text-neutral50', bg: '#F0F0F4' }
}

/**
 * Os quatro estados do registro. Ícone E cor mudam juntos nos quatro: cor
 * sozinha não passa em daltonismo, e a bolinha não tem rótulo visível.
 *
 * `PENDING` é ampulheta cinza-azul, e não vermelho: existe registro enviado e
 * ainda não auditado, então cobrar o médico seria cobrar algo em curso.
 */
const REGISTRATION_CONFIG: Record<RegistrationStatusValue, StateConfig> = {
  [EffectiveRegistrationStatus.OK]: { icon: mdiCheck, colorClass: 'text-sucess', bg: '#EAFBEF' },
  [EffectiveRegistrationStatus.PARTIAL]: {
    icon: mdiAlert,
    colorClass: 'text-alert',
    bg: '#FDF0DF'
  },
  [EffectiveRegistrationStatus.PENDING]: {
    icon: mdiTimerSand,
    colorClass: 'text-slate-500',
    bg: '#EEF2F8'
  },
  [EffectiveRegistrationStatus.NONE]: {
    icon: mdiClose,
    colorClass: 'text-negative',
    bg: '#FCE9EA'
  },
  // Q1 — plantão fora do time: registro não exigido, traço cinza neutro.
  [EffectiveRegistrationStatus.NOT_REQUIRED]: {
    icon: mdiMinus,
    colorClass: 'text-neutral50',
    bg: '#F0F0F4'
  }
}

// Valor desconhecido (estado novo do backend) NÃO cai num ícone inventado: volta
// para a bolinha de conformidade, que é sempre verdadeira.
const registrationConfig = computed(() =>
  props.registrationStatus
    ? (REGISTRATION_CONFIG[props.registrationStatus as RegistrationStatusValue] ?? null)
    : null
)

const config = computed(() => registrationConfig.value ?? CONFIG[props.state])

const tooltipText = computed(() => {
  const stateTitle = registrationConfig.value
    ? REGISTRATION_STATUS_LABELS[props.registrationStatus as RegistrationStatusValue]
    : COMPLIANCE_STATE_TITLES[props.state]
  return props.domainLabel ? `${props.domainLabel} — ${stateTitle}` : stateTitle
})
</script>
